<template>
  <div class="product-card__shop-offer">
    <div class="shop icons">
      <div class="shop__name--wrap">
        <div class="shop__name body-m-regular">
          {{ shop }}
        </div>
      </div>

      <div :class="{ shop__name__proved: isProver }"></div>
      <shop-rating :shop="model" class="list-model" />
      <Currency
        type="new"
        :value="model.price"
        class="offer-price heading-xs"
      />
    </div>
    <div v-if="delivery.cost" class="delivery icons body-m-regular">
      <span class="day-text body-m-regular">{{ delivery.dayText }}</span>
      <span
        :class="[{ green: delivery.cost === 'бесплатно' }, 'body-m-regular']"
        >{{ delivery.cost }}</span
      >
    </div>
  </div>
</template>

<script lang="ts" setup>
import Currency from '~/components/ui/currency/Currency.vue';
import {
  getDeliveryDateRange,
  getWeekDayName,
  getFutureDay,
} from '~/utils/dateUtils';

const shopRating = defineAsyncComponent(
  () => import('~/components/ui/shop-rating/ShopRating.vue')
);

const props = defineProps({
  model: {
    type: Object,
    required: true,
  },
  isShowShop: {
    type: Boolean,
    default: true,
  },
});
const isProver = computed(() => {
  return props.model?.shop_info?.proved_merchant;
});
// текст доставки
const delivery = computed<{ cost: string; dayText: string }>(() => {
  if (!props.model.delivery) return { cost: '', dayText: '' };

  // через сколько дней минимально доставка
  const day: number | null =
    props.model?.delivery?.options && props.model?.delivery?.options.length > 0
      ? props.model.delivery.options.reduce(
          (day: number | null, item: { days: string | undefined }) => {
            if (item.days) {
              const interval = item.days.split(/\D/).map((d) => Number(d));
              if (
                interval &&
                interval.length &&
                (day === null || interval[0] < day)
              )
                return interval[0];
            }
            return day;
          },
          null
        )
      : null;

  // рассчет минимальной доставки из delivery.options
  const getDeliveryOptionsCost = (arr: { cost: number | null }[]) => {
    const min = Math.min(...arr.map((item) => (item.cost ? item.cost : 0)));
    return min ? `от\u00A0${min}\u00A0₽` : min === 0 ? 'бесплатно' : 'есть';
  };

  const getCost = () => {
    if (props.model.delivery.cost === null) return 'Есть';
    if (!props.model.delivery.cost)
      return props.model.delivery?.options &&
        props.model.delivery?.options.length
        ? getDeliveryOptionsCost(props.model.delivery.options)
        : 'есть';
    return `${props.model.delivery.fixed ? '' : 'от\u00A0'}${
      props.model.delivery.cost
    }\u00A0₽`;
  };

  // цена доставки
  const cost: string = getCost();

  // текст доставки по дате
  let dayText = '';
  if (day !== null && day < 3) {
    dayText = `${getFutureDay(day)} - `;
  } else if (day !== null) {
    const date = new Date();
    date.setDate(date.getDate() + day);
    dayText = `${getWeekDayName(date)}, ${getDeliveryDateRange(
      date.toDateString()
    ).toLowerCase()} - `;
  }

  return {
    dayText,
    cost,
  };
});

// Магазин
const shop = computed(() => {
  return props.model.shop_info.name || '';
});
</script>

<style scoped lang="scss">
.product-card__shop-offer {
  .shop {
    padding-left: 0;
    width: 100%;
    display: flex;
    gap: 4px;
    row-gap: 2px;
    flex-wrap: wrap;
    margin: 0;

    .offer-price {
      flex-grow: 1;
      text-align: right;
    }

    .shop__name--wrap {
      display: flex;
      align-items: center;
      max-width: 52px;
      @include xl-block {
        max-width: 118px;
      }
    }

    &__name {
      color: $main-cta;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      flex-grow: 2;
    }

    .shop__name__proved {
      position: relative;
      margin-right: 20px;

      &:after {
        position: absolute;
        top: calc(50% - 7px);
        right: -13px;
        content: ' ';
        width: 14px;
        height: 14px;
        background: url('/static/images/icon/proved.svg') center / contain
          no-repeat;
        @include lg-block {
          top: calc(50% - 8px);
          right: -15px;
          width: 16px;
          height: 16px;
        }
        @include xl-block {
          top: calc(50% - 7px);
          width: 15px;
          height: 14px;
        }
        @include xxl-block {
          top: calc(50% - 8px);
          right: -16px;
          width: 16px;
          height: 16px;
        }
      }
    }

    .proved-merchant {
      margin-left: 4px;
      padding-left: 0;
      margin-bottom: 0;
    }

    @include md-block {
      .proved-merchant {
        margin-top: -2px;
      }
    }

    :deep(.shop-rating) {
      .review-count {
        display: none;
      }
    }
  }

  .delivery {
    position: relative;
    width: 100%;
    color: $achromatic-disabled;

    .day-text {
      text-transform: capitalize;
    }

    &.icons {
      padding-left: 20px;

      &:after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 16px;
        height: 16px;
        background-position: center center;
        background-size: 100%;
        background-repeat: no-repeat;
        display: block;
        background-image: url('/static/images/icon/delivery.svg');
      }

      :deep(.shop-rating) {
        img {
          margin: 0 4px 0 8px;
        }
      }
    }

    @include lg-block {
      padding-left: 22px;
      &.icons {
        &:after {
          top: 2px;
        }
      }
    }
  }

  .green {
    color: $green4;
  }
}
</style>
